import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import type { AdminUserSearchUser } from '@/client/types/admin/users-orgs';
import { usePatchRepMutation, useUpsertRepMutation } from '@/client/api/sales/sales';
import { useAdminUserDetailQuery } from '@/client/api/admin/users.query';
import { useHrEmployeeByUidQuery } from '@/client/api/hr/people';
import { Link } from 'react-router-dom';
import { useAssignUserRoleMutation, useRoleTypesQuery } from '@/client/api/admin/roles';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS, SUPER_ADMIN_ROLE } from '@/client/config/permissions';
import {
  SALES_VERTICALS,
  VERTICAL_LABEL,
  type SalesRep,
  type SalesRepRole,
  type SalesVertical,
} from '@/client/types/sales';
import { Segmented, btnGhost, btnPrimary, initials, toastSales } from '../sales-ui';

type Desk = SalesVertical | 'all';

// Floor role only changes who may move claims (actor.py `reassign_allowed`);
// the sales.* permissions themselves come from the person's admin role.
const ROLES: { id: SalesRepRole; label: string; detail: string }[] = [
  { id: 'agent', label: 'Agent', detail: 'Works their own prospects, claims and outreach.' },
  { id: 'lead', label: 'Lead', detail: 'Agent, plus moves claims between reps on their own desk.' },
  { id: 'manager', label: 'Manager', detail: 'Moves claims between anyone, on every desk.' },
];

const DESKS: { id: Desk; label: string }[] = [
  { id: 'all', label: 'All desks' },
  ...SALES_VERTICALS.map((v) => ({ id: v as Desk, label: VERTICAL_LABEL[v] })),
];

type Person = { uid: number; name: string; email: string };

function fromSearch(u: AdminUserSearchUser): Person {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return { uid: u.uid, name: name || u.email, email: u.email };
}

interface RepFormDialogProps {
  /** Present when editing; the person is then fixed. */
  rep?: SalesRep | null;
  roster: SalesRep[];
  onClose: () => void;
}

export function RepFormDialog({ rep, roster, onClose }: RepFormDialogProps) {
  const upsert = useUpsertRepMutation();
  const patch = usePatchRepMutation();
  const editing = Boolean(rep);

  const [query, setQuery] = useState('');
  const [person, setPerson] = useState<Person | null>(
    rep ? { uid: rep.uid ?? 0, name: rep.name, email: rep.email ?? '' } : null,
  );
  const [role, setRole] = useState<SalesRepRole>(rep?.role ?? 'agent');
  const [desk, setDesk] = useState<Desk>(rep?.desk ?? 'homes');
  const [title, setTitle] = useState(rep?.title ?? '');

  const existing = !editing && person ? roster.find((r) => r.uid === person.uid) : undefined;

  // A floor row alone does not let anyone in: console login needs `admin=1`,
  // and the sales pages need a sales_* role (or super). Say what is missing and
  // offer to fix it here rather than leave a rep who cannot sign in.
  const detail = useAdminUserDetailQuery(person?.uid ?? null, { enabled: Boolean(person) });
  const { data: roleTypes = [] } = useRoleTypesQuery();
  const assignRole = useAssignUserRoleMutation();
  const hrEmployee = useHrEmployeeByUidQuery(person?.uid ?? null, Boolean(person));
  const canAssignRole = useGate(PERMISSIONS.role.create).allowed;

  const account = detail.data;
  const salesRoleName = `sales_${role}`;
  const salesRoleType = roleTypes.find((t) => t.name === salesRoleName);
  const heldRoleNames = (account?.roles ?? [])
    .filter((a) => a.active === 1)
    .map((a) => a.user_account_role_type?.name ?? roleTypes.find((t) => t.uartid === a.uartid)?.name);
  const needsConsole = Boolean(account) && account!.user.admin !== 1;
  const needsRole =
    Boolean(account) &&
    !heldRoleNames.some((n) => n === SUPER_ADMIN_ROLE || n?.startsWith('sales_'));

  const [grantRole, setGrantRole] = useState(false);
  useEffect(() => setGrantRole(needsRole && canAssignRole && Boolean(salesRoleType)), [
    needsRole,
    canAssignRole,
    salesRoleType,
  ]);

  const pending = upsert.isPending || patch.isPending || assignRole.isPending;

  const grantAccess = async (p: Person) => {
    const reason = `Added to the sales floor as ${role}`;
    if (grantRole && salesRoleType) {
      try {
        await assignRole.mutateAsync({ uid: p.uid, uartids: [salesRoleType.uartid], notes: reason });
      } catch (err) {
        toast.error(`Could not assign ${salesRoleName}: ${(err as Error).message}`);
      }
    }
  };

  const save = () => {
    if (!person) {
      toast.error('Search for and select a person first.');
      return;
    }
    const fields = { role, desk, title: title.trim() };
    const done = {
      onSuccess: async () => {
        await grantAccess(person);
        toast.success(editing || existing ? `${person.name} updated` : `${person.name} added to the floor`);
        onClose();
      },
      onError: (err: unknown) => toastSales(err),
    };
    if (rep) {
      patch.mutate({ id: rep.id, ...fields }, done);
    } else {
      upsert.mutate(
        { uid: person.uid, name: person.name, email: person.email || undefined, ...fields },
        done,
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg gap-5">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${rep?.name}` : 'Add a rep'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Change their level, desk or title.'
              : 'Pick the account they use to sign in to admin — that is how the floor knows it is them.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-gray-700">Person</p>
          {person ? (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-white">
                {initials(person.name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{person.name}</p>
                <p className="truncate text-xs text-gray-500">
                  {person.email || 'No email'} · uid {person.uid}
                </p>
              </div>
              {!editing && (
                <button
                  type="button"
                  className="text-xs font-medium text-blue-700 hover:underline"
                  onClick={() => {
                    setPerson(null);
                    setQuery('');
                  }}
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            <UserSearchSelect
              value={query}
              onChange={setQuery}
              onSelect={(u) => {
                const p = fromSearch(u);
                setPerson(p);
                const match = roster.find((r) => r.uid === p.uid);
                if (match) {
                  setRole(match.role);
                  setDesk(match.desk);
                  setTitle(match.title);
                }
              }}
            />
          )}
          {existing && (
            <p className="text-xs text-amber-700">Already on the team — saving updates their level, desk and title.</p>
          )}
          {(needsConsole || needsRole) && (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <p className="text-xs font-medium text-amber-900">
                {person?.name} cannot use the sales floor yet.
              </p>
              {needsConsole && (
                <p className="text-xs text-amber-900">
                  They cannot sign in to admin yet. Console access is granted by HR on their{' '}
                  {hrEmployee.data ? (
                    <Link
                      to={`/admin/human-resource/people/directory/${hrEmployee.data.id}`}
                      className="font-medium underline"
                    >
                      employee profile
                    </Link>
                  ) : (
                    <Link to="/admin/human-resource/people/directory" className="font-medium underline">
                      employee profile (add them to the directory first)
                    </Link>
                  )}
                  .
                </p>
              )}
              {needsRole && (
                <label className="flex items-start gap-2 text-xs text-amber-900">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-slate-900"
                    checked={grantRole}
                    disabled={!canAssignRole || !salesRoleType}
                    onChange={(e) => setGrantRole(e.target.checked)}
                  />
                  <span>
                    Assign the <code>{salesRoleName}</code> role, which carries the sales permissions.
                    {!canAssignRole && ' You need permission to assign roles.'}
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        <fieldset className="space-y-1.5">
          <legend className="mb-1.5 text-xs font-medium text-gray-700">Level</legend>
          <div className="grid gap-2">
            {ROLES.map((r) => (
              <label
                key={r.id}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors',
                  role === r.id ? 'border-slate-900 bg-slate-50' : 'border-gray-200 hover:bg-gray-50',
                )}
              >
                <input
                  type="radio"
                  name="rep-role"
                  className="mt-0.5 accent-slate-900"
                  checked={role === r.id}
                  onChange={() => setRole(r.id)}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{r.label}</span>
                  <span className="block text-xs text-gray-500">{r.detail}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-gray-700">Desk</p>
          <div className="overflow-x-auto">
            <Segmented value={desk} onChange={setDesk} options={DESKS} />
          </div>
          {role === 'lead' && desk === 'all' && (
            <p className="text-xs text-amber-700">A lead on All desks cannot move claims — pick the one desk they lead.</p>
          )}
        </div>

        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-gray-700">
            Title <span className="font-normal text-gray-400">(optional)</span>
          </span>
          <input
            className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Sales Lead, Homes"
          />
        </label>

        <DialogFooter className="gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={btnPrimary} disabled={pending || !person} onClick={save}>
            {pending ? 'Saving…' : editing || existing ? 'Save changes' : 'Add rep'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
